import _invariant            from 'invariant';
import { createSelector }    from 'reselect';
import url                   from '../utils/url';
import { prepareRequestKey } from '../reducers/utils/apiResources/prepareRequestKey';
import deprecated            from '../utils/deprecated';


deprecated(
  "The 'resourceSelector' module is deprecated.  You should use the 'apiDataSelectors' module instead. " +
  "This will require modules to be updated to use the new apiDataReducers."
);


export const selectAllResourcesState = (state) => state.get('apiResources');


/**
 * Creates a selector for the raw state for the resource in question
 *
 * @param resourceName
 *
 * @return {function(state: object, props: object)}
 */
export const createSelectResource = (resourceName) => createSelector(
  selectAllResourcesState,
  allResourcesState => allResourcesState.get(resourceName)
);


// -----------------------------------------------------------------------------------------------------------------
// Items
// -----------------------------------------------------------------------------------------------------------------


/**
 * Selector for resource 'items' state.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: object)}
 */
export const createSelectResourceItems = (resourceName) => {

  const selectResource = createSelectResource(resourceName);

  return createSelector(
    selectResource,
    (resourceState) => resourceState.get('items')
  );

};


/**
 * Creates a selector for all resource items by Id.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceItemsByIds = (resourceName) => {

  const selectResourceItems = createSelectResourceItems(resourceName);

  return createSelector(
    selectResourceItems,
    (resourceState) => resourceState.get('byIds')
  );

};


/**
 * Creates a selector for all resource items by slug.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectAllResourceItemsBySlug = (resourceName) => {

  const selectResourceItems      = createSelectResourceItems(resourceName);
  const selectResourceItemsByIds = createSelectResourceItemsByIds(resourceName);

  return createSelector(
    selectResourceItems,
    selectResourceItemsByIds,
    (resourceState, itemsById) => resourceState.get('idsBySlug').map(id => itemsById.get(id))
  );

};


/**
 * Creates a selector for a resource, requested by `resourceId`
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {resourceId: string})}
 */
export const createSelectResourceAllItemById = (resourceName) => {

  const selectResourceItemsByIds = createSelectResourceItemsByIds(resourceName);

  return createSelector(
    (state, {resourceId}) => resourceId,
    selectResourceItemsByIds,
    (id, itemsById) => itemsById.get(id) || null
  );

};


/**
 * Creates a selector for a resource item, requested by `resourceSlug`.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {resourceSlug})}
 */
export const createSelectResourceItemBySlug = (resourceName) => {

  const selectResourceItemsBySlug = createSelectAllResourceItemsBySlug(resourceName);

  return createSelector(
    (state, {resourceSlug}) => resourceSlug,
    selectResourceItemsBySlug,
    (slug, itemsBySlug) => (itemsBySlug.get(slug) || null)
  );

};


// -----------------------------------------------------------------------------------------------------------------
// List
// -----------------------------------------------------------------------------------------------------------------


/**
 * Creates a selector for the raw 'list' state.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceListState = (resourceName) => {

  const selectResource = createSelectResource(resourceName);

  return createSelector(
    selectResource,
    (resourceState) => resourceState.get('list'),
  );

};


/**
 * Creates a selector for the ID's of items, by list page.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceListPageItemIds = (resourceName) => {

  const selectResourceListState = createSelectResourceListState(resourceName);

  return createSelector(
    selectResourceListState,
    (resourceListState) => resourceListState.get('idsByPage'),
  );

};


/**
 * Creates a selector for the ids of items for a particular list page.
 *
 * For the full items, not just the ids, use createSelectResourceListPage()
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {apiUrl: string})}
 */
export const createSelectResourceListPageItemIdsForPage = (resourceName) => {

  const selectResourceListPageItemIds = createSelectResourceListPageItemIds(resourceName);

  return createSelector(
    (state, {apiUrl}) => apiUrl,
    selectResourceListPageItemIds,
    (apiUrl, resourceListPageItemIds) => {

      if (!resourceListPageItemIds.has(apiUrl)) {

        return null;

      }

      return resourceListPageItemIds.get(apiUrl);

    }
  );

};


/**
 * Creates a selector for the page counts for all list pages, by page URL.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceListPageCountsByUrl = (resourceName) => {

  const selectResourceListState = createSelectResourceListState(resourceName);

  return createSelector(
    selectResourceListState,
    (resourceListState) => resourceListState.get('pageCounts')
  );

};


export const createSelectResourceListPageCountsForPage = (resourceName) => {

  const selectResourceListPageCountsByUrl = createSelectResourceListPageCountsByUrl(resourceName);

  return createSelector(
    (state, {apiUrl}) => url.removeQueryParams(apiUrl, {page: true}).replace(/\?$/, ''),
    selectResourceListPageCountsByUrl,
    (apiUrl, pageCountsByUrl) => pageCountsByUrl.get(apiUrl) || null,
  );
};


/**
 * Creates a selector for all items for a page
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {apiUrl: string})}
 */
export const createSelectResourceListPage = (resourceName) => {

  const selectResourceListPageItemIdsForPage = createSelectResourceListPageItemIdsForPage(resourceName);
  const selectResourceItemsByIds             = createSelectResourceItemsByIds(resourceName);

  return createSelector(
    selectResourceListPageItemIdsForPage,
    selectResourceItemsByIds,
    (itemIds, itemsById) => {

      if (null === itemIds || null === itemsById) {

        return null;

      }

      return itemIds.map((id) => itemsById.get(id));

    }
  );
};


// -----------------------------------------------------------------------------------------------------------------
// InProgress
// -----------------------------------------------------------------------------------------------------------------


/**
 * Creates a selector for the `inProgress` state.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceInProgressState = (resourceName) => {

  const selectResource = createSelectResource(resourceName);

  return createSelector(
    selectResource,
    (resourceState) => resourceState.get('inProgress')
  );

};


/**
 * Creates a selector for the `inProgress` flag for a particular URL (create, get, update or delete).
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {method: string, url: string})}
 */
export const createSelectResourceInProgressForApiUrl = (resourceName) => {

  const selectResourceInProgressState = createSelectResourceInProgressState(resourceName);

  return createSelector(
    (state, props) => props,
    selectResourceInProgressState,
    (props, inProgressState) => {

      _invariant(props.method, 'Missing method');

      _invariant(props.url, 'Missing url');

      return inProgressState.includes(prepareRequestKey(props));

    }
  );

};


// -----------------------------------------------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------------------------------------------


/**
 * Creates a selector for `error` state (all errors, by apiURL).
 *
 * @param {string} resourceName
 *
 * @return {function(state: object)}
 */
export const createSelectResourceErrorsState = (resourceName) => {

  const selectResource = createSelectResource(resourceName);

  return createSelector(
    selectResource,
    (resourceState) => resourceState.get('errors')
  );

};


/**
 * Creates a selector for the error for a particular apiURL.
 *
 * @param {string} resourceName
 *
 * @return {function(state: object, props: {method: string, url: string})}
 */
export const createSelectResourceErrorForApiUrl = (resourceName) => {

  const selectResourceErrorsState = createSelectResourceErrorsState(resourceName);

  return createSelector(
    (state, props) => props,
    selectResourceErrorsState,
    (props, errorsState) => {

      _invariant(props.method, 'Missing method');

      _invariant(props.url, 'Missing url');

      return errorsState.get(prepareRequestKey(props)) || null;

    }
  );

};



// WEBPACK FOOTER //
// ./src/selectors/resourceSelectors.js